import { useEffect, useState } from 'react';
import {
  fetchMissionEditors,
  inviteMissionEditor,
  removeMissionEditor,
  searchEditorCandidates,
  type MissionEditorEntry,
} from '../api/missions';
import { ca } from '../i18n/ca';
import { AccountPicker } from './AccountPicker';
import { EDITORS_KEY, readStored, writeStored, type DraftEditor } from './newMissionDraft';
import { ApiError } from '../api/http';

/**
 * Who can edit a mission besides its author (roadmap item `edicio-a-diverses-mans`).
 *
 * Without a `code` it is the wizard's list: the mission does not exist yet, so the members picked
 * are kept in the draft and the last step adds them once it has generated. With a `code` it is
 * the mission's own list: everybody who can edit sees it, and its author or an admin invites and
 * removes; an invited editor may take themselves off.
 */
export function InvitedEditors({ code, myId }: { code?: string; myId?: number | null }) {
  const t = ca.missionUi.editors;
  const [typed, setTyped] = useState('');
  const [draft, setDraft] = useState<DraftEditor[]>(() =>
    code ? [] : (readStored<DraftEditor[]>(EDITORS_KEY) ?? []),
  );
  const [live, setLive] = useState<MissionEditorEntry[]>([]);
  const [canManage, setCanManage] = useState(!code);
  const [loaded, setLoaded] = useState(!code);
  const [problem, setProblem] = useState<string | null>(null);
  const [loadProblem, setLoadProblem] = useState(false);

  useEffect(() => {
    if (!code) return;
    let alive = true;
    fetchMissionEditors(code)
      .then((v) => {
        if (!alive) return;
        setLive(v.editors);
        setCanManage(v.canManage);
        setLoaded(true);
      })
      .catch(() => {
        if (alive) setLoadProblem(true);
      });
    return () => {
      alive = false;
    };
  }, [code]);

  const rows: { key: string; handle: string; displayName: string; owner: boolean; id?: number }[] =
    code
      ? live.map((e) => ({
          key: String(e.accountId),
          handle: e.handle,
          displayName: e.displayName,
          owner: e.owner,
          id: e.accountId,
        }))
      : draft.map((e) => ({
          key: e.handle,
          handle: e.handle,
          displayName: e.displayName,
          owner: false,
        }));

  async function add(handle: string, displayName: string) {
    const clean = handle.trim().replace(/^@/, '');
    if (!clean) return;
    setProblem(null);
    if (!code) {
      if (!draft.some((e) => e.handle.toLowerCase() === clean.toLowerCase())) {
        const next = [...draft, { handle: clean, displayName }];
        setDraft(next);
        writeStored(EDITORS_KEY, next);
      }
      setTyped('');
      return;
    }
    try {
      const v = await inviteMissionEditor(code, clean);
      setLive(v.editors);
      setTyped('');
    } catch (err) {
      setProblem(err instanceof ApiError && err.status === 400 ? t.inviteRefused : t.inviteFailed);
    }
  }

  async function remove(row: (typeof rows)[number]) {
    setProblem(null);
    if (!code) {
      const next = draft.filter((e) => e.handle !== row.handle);
      setDraft(next);
      writeStored(EDITORS_KEY, next);
      return;
    }
    try {
      const v = await removeMissionEditor(code, row.id!);
      setLive(v.editors);
      setCanManage(v.canManage);
    } catch {
      setProblem(t.removeFailed);
    }
  }

  const exclude = rows.map((r) => r.handle);
  const mayRemove = (r: (typeof rows)[number]) =>
    !r.owner && (canManage || (code && r.id === myId));

  return (
    <section className="card invited-editors" aria-label={t.title}>
      <h3>{t.title}</h3>
      <p className="muted small">{code ? t.hintLive : t.hintDraft}</p>
      {!loaded && !loadProblem && <p className="muted small">{t.loading}</p>}
      {loadProblem && <p className="muted small">{t.loadFailed}</p>}
      {rows.length === 0 && loaded && !loadProblem && <p className="muted small">{t.none}</p>}
      {rows.length > 0 && (
        <ul className="plain invited-editors-list">
          {rows.map((r) => (
            <li key={r.key}>
              <i className={`bi ${r.owner ? 'bi-person-badge' : 'bi-person'}`} aria-hidden="true" />
              <span className="grow">
                {r.displayName} <span className="muted small">@{r.handle}</span>
                {r.owner && <span className="chip">{t.author}</span>}
              </span>
              {mayRemove(r) && (
                <button
                  type="button"
                  className="danger"
                  aria-label={t.remove(r.displayName)}
                  title={t.remove(r.displayName)}
                  onClick={() => void remove(r)}
                >
                  <i className="bi bi-x-lg" aria-hidden="true" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {canManage && (
        <AccountPicker
          value={typed}
          onChange={setTyped}
          onPick={(c) => void add(c.handle, c.displayName)}
          search={(q) => searchEditorCandidates(q, code)}
          label={t.field}
          exclude={exclude}
        />
      )}
      {problem && (
        <p className="notice warn small" role="alert">
          {problem}
        </p>
      )}
    </section>
  );
}
